import React from 'react';
import { cn } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { CashflowProvider } from '../../api/cashflow';
import { PROVIDER_LABELS, PROVIDER_ORDER } from './utils';

export interface ReceiptFormData {
  date: string;
  provider: CashflowProvider;
  amount: string; // ввод как строка (допускает запятую)
  comment: string;
}

interface ReceiptDialogProps {
  isDark: boolean;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  formData: ReceiptFormData;
  onFormDataChange: (updater: (prev: ReceiptFormData) => ReceiptFormData) => void;
  onSubmit: () => void;
  title: string;
  description: string;
  submitLabel: string;
}

export function ReceiptDialog({
  isDark,
  isOpen,
  onOpenChange,
  formData,
  onFormDataChange,
  onSubmit,
  title,
  description,
  submitLabel,
}: ReceiptDialogProps) {
  const inputClass = cn(
    isDark ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-slate-200',
  );

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn('max-w-lg', isDark ? 'bg-[#1E293B] border-slate-700' : 'bg-white')}
      >
        <DialogHeader>
          <DialogTitle className={cn(isDark ? 'text-white' : 'text-[#0F172A]')}>
            {title}
          </DialogTitle>
          <DialogDescription className={cn(isDark ? 'text-slate-400' : 'text-slate-500')}>
            {description}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className={cn(isDark ? 'text-slate-300' : 'text-slate-700')}>Дата *</Label>
              <Input
                type="date"
                value={formData.date}
                onChange={(e) =>
                  onFormDataChange((prev) => ({ ...prev, date: e.target.value }))
                }
                className={inputClass}
              />
            </div>
            <div className="space-y-2">
              <Label className={cn(isDark ? 'text-slate-300' : 'text-slate-700')}>Провайдер *</Label>
              <Select
                value={formData.provider}
                onValueChange={(value: CashflowProvider) =>
                  onFormDataChange((prev) => ({ ...prev, provider: value }))
                }
              >
                <SelectTrigger className={inputClass}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PROVIDER_ORDER.map((p) => (
                    <SelectItem key={p} value={p}>
                      {PROVIDER_LABELS[p]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label className={cn(isDark ? 'text-slate-300' : 'text-slate-700')}>
              Сумма поступления *
            </Label>
            <Input
              type="text"
              inputMode="decimal"
              value={formData.amount}
              onChange={(e) =>
                onFormDataChange((prev) => ({ ...prev, amount: e.target.value }))
              }
              placeholder="123 123,45"
              className={inputClass}
            />
            <p className={cn('text-xs', isDark ? 'text-slate-500' : 'text-slate-400')}>
              Сумма, реально пришедшая на счёт (с копейками через запятую).
            </p>
          </div>

          <div className="space-y-2">
            <Label className={cn(isDark ? 'text-slate-300' : 'text-slate-700')}>Комментарий</Label>
            <Textarea
              value={formData.comment}
              onChange={(e) =>
                onFormDataChange((prev) => ({ ...prev, comment: e.target.value }))
              }
              placeholder="напр. Click перевод 28.06 / № платёжки"
              rows={2}
              className={inputClass}
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className={cn(
              isDark ? 'border-slate-700 text-slate-300 hover:bg-slate-800' : 'border-slate-200',
            )}
          >
            Отмена
          </Button>
          <Button onClick={onSubmit} className="bg-[#0EA5A4] hover:bg-[#0EA5A4]/90">
            {submitLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
